import { getContentHeight } from 'components/sections/Navigation/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import useDialog from 'store/dialog';
import useSport from 'store/sport';
import useSelectedTheme from 'store/theme';
import useScreen from 'hooks/useScreen';
import MobileFooter, { SportNavigation } from '../MobileSportFooter';
import { useSearchParams } from 'react-router-dom';
import useWindowResize from 'hooks/useWindowResize';
import SectionLoader from 'components/sections/SectionLoader/SectionLoader';

const LOGIN = 'ui:signin';
const REGISTER = 'ui:signup';
const THEME_CHANGE = 'ui:theme_change';
const GOTO = 'ui:goto';
const BETSLIP_OPENED = 'ui:betslip_opened';
const ROUTER_CHANGE = 'router:change_location';
const BETSLIP_TOGGLE = 'ui:betslip_toggle';

const SPORT_TAB_PARAM = 'tab';
const MATCH_PARAM = 'match';
const ATLAS_MATCH_PARAM = 'e';
const BETSLIP_OPENED_PARAM = 'betslip_opened';

enum BetslipStateEnum {
  opened = '1',
}

const Atlas = ({ sportId }: { sportId: string }) => {
  const ref = useRef(null);
  const { isMobile } = useScreen();
  const [, dialogActions] = useDialog();
  const [themeState] = useSelectedTheme();
  const [sportState] = useSport();
  const [isLoaded, setIsLoaded] = useState(false);
  const urlParsed = useRef(false);
  const [betslipOpened, setBetslipOpened] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!isLoaded || urlParsed.current) return;

    urlParsed.current = true;
    const activeTab = searchParams.get(SPORT_TAB_PARAM);
    const isBetslipOpened = searchParams.get(BETSLIP_OPENED_PARAM) === BetslipStateEnum.opened;

    if (isBetslipOpened) toggleBetslip();

    if (activeTab === SportNavigation.live) return gotoLive();

    if (activeTab === SportNavigation.sport) return gotoSport();

    if (activeTab === SportNavigation.results) return gotoResults();

    gotoLive();
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      ref.current.contentWindow.postMessage(
        {
          action: THEME_CHANGE,
          payload: themeState.isDark ? 'dark' : 'light',
        },
        '*'
      );
    }
  }, [themeState.isDark, isLoaded]);

  const getMatchSearch = () => {
    const search = searchParams.get(MATCH_PARAM);
    return search ? { search: `?${ATLAS_MATCH_PARAM}=${search}` } : {};
  };

  const gotoLive = () => {
    if (!isLoaded) return;
    betslipOpened && toggleBetslip();
    ref.current.contentWindow.postMessage(
      {
        action: GOTO,
        payload: {
          pathname: `/${SportNavigation.live}`,
          ...getMatchSearch(),
        },
      },
      '*'
    );

    setSearchParams((prevSearchParams) => {
      prevSearchParams.set(SPORT_TAB_PARAM, SportNavigation.live);
      return prevSearchParams;
    });
  };

  const gotoSport = () => {
    if (!isLoaded) return;
    betslipOpened && toggleBetslip();
    ref.current.contentWindow.postMessage(
      {
        action: GOTO,
        payload: {
          pathname: `/${SportNavigation.sport}`,
          ...getMatchSearch(),
        },
      },
      '*'
    );

    setSearchParams((prevSearchParams) => {
      prevSearchParams.set(SPORT_TAB_PARAM, SportNavigation.sport);
      return prevSearchParams;
    });
  };

  const gotoResults = () => {
    if (!isLoaded) return;
    betslipOpened && toggleBetslip();
    ref.current.contentWindow.postMessage(
      {
        action: GOTO,
        payload: {
          pathname: `/${SportNavigation.results}`,
          ...getMatchSearch(),
        },
      },
      '*'
    );
    setSearchParams((prevSearchParams) => {
      prevSearchParams.set(SPORT_TAB_PARAM, SportNavigation.results);
      return prevSearchParams;
    });
  };

  const toggleBetslip = () => {
    if (!isLoaded) return;
    ref.current.contentWindow.postMessage(
      {
        action: BETSLIP_TOGGLE,
      },
      '*'
    );
  };

  useEffect(() => {
    if (!sportState.data[sportId]) return;

    const handler = (event) => {
      const { hostname } = new URL(sportState.data[sportId]?.url);
      if (event.origin.includes(hostname)) {
        switch (event.data.action) {
          case LOGIN:
            return dialogActions.set('signIn');
          case REGISTER:
            return dialogActions.set('signUp');
          case ROUTER_CHANGE: {
            const { search } = event.data.payload;
            const atlasSearchParams = new URLSearchParams(search);
            const match = atlasSearchParams.get(ATLAS_MATCH_PARAM);

            if (!match) return;

            return setSearchParams((prevSearchParams) => {
              prevSearchParams.set(MATCH_PARAM, match);
              return prevSearchParams;
            });
          }
          case BETSLIP_OPENED:
            if (isMobile && urlParsed.current) {
              setBetslipOpened(event.data.payload);

              setSearchParams((prevSearchParams) => {
                event.data.payload
                  ? prevSearchParams.set(BETSLIP_OPENED_PARAM, BetslipStateEnum.opened)
                  : prevSearchParams.delete(BETSLIP_OPENED_PARAM);
                return prevSearchParams;
              });
            }
            return;
          default:
            return null;
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sportState.data[sportId]]);

  const [height, setHeight] = useState<number>();

  const handleWindowHeight = useCallback(() => {
    setHeight(getContentHeight());
  }, []);

  useWindowResize(handleWindowHeight);

  if (!sportState.data[sportId]) return <SectionLoader />;

  const activeTab = searchParams.get(SPORT_TAB_PARAM);
  const isBetslipOpened = searchParams.get(BETSLIP_OPENED_PARAM) === BetslipStateEnum.opened;
  const currentPage = isBetslipOpened ? SportNavigation.betslip : (activeTab as SportNavigation);

  return (
    <>
      {sportState.data[sportId] && (
        <iframe
          ref={ref}
          src={sportState.data[sportId]?.url}
          onLoad={() => {
            setIsLoaded(true);
            setHeight(getContentHeight());
          }}
          style={{ width: '100%', border: 0, height }}
        />
      )}
      {isMobile && (
        <MobileFooter
          onLiveClick={gotoLive}
          onSportClick={gotoSport}
          currentPage={currentPage}
          onResultsClick={gotoResults}
          onToggleClick={toggleBetslip}
        />
      )}
    </>
  );
};

export default Atlas;
